import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calculator, FileText, TrendingUp } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './CorporateIncomeTaxCalculatorPage/components/CalculationForm';
import ResultsOverview from './CorporateIncomeTaxCalculatorPage/components/ResultsOverview';
import CalculationDetails from './CorporateIncomeTaxCalculatorPage/components/CalculationDetails';
import QuickAdjustmentSidebar from './CorporateIncomeTaxCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './CorporateIncomeTaxCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './CorporateIncomeTaxCalculatorPage/components/TipsSidebar';
import InfoContent from './CorporateIncomeTaxCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Основные параметры
  revenue: '10000000', // Выручка от реализации
  otherIncome: '500000', // Внереализационные доходы
  expensesRelated: '7000000', // Расходы, связанные с производством и реализацией
  expensesOther: '300000', // Внереализационные расходы
  
  // Дополнительные параметры
  taxationYear: new Date().getFullYear().toString(), // Налоговый период (год)
  taxRegion: 'default', // Регион (для расчета региональной ставки)
  advancePayments: true, // Учитывать авансовые платежи
  advancePaymentMethod: 'quarter', // Метод расчета авансовых платежей (quarter/month)
  
  // Специальные вычеты и льготы
  specialDeductions: false, // Использовать специальные вычеты
  researchAndDevelopment: '0', // Расходы на НИОКР (научно-исследовательские и опытно-конструкторские разработки)
  investmentDeduction: '0', // Инвестиционный вычет
  lossesCarriedForward: '0', // Убытки прошлых лет
  
  // Дополнительные льготы
  specialTaxRegimes: false, // Особые налоговые режимы
  isITCompany: false, // IT-компания (льготная ставка)
  isResidentSpecialZone: false, // Резидент особой экономической зоны
  specialZoneType: 'none', // Тип особой экономической зоны
  
  // Дивиденды
  dividends: false, // Учитывать дивиденды
  dividendsIncome: '0', // Доходы в виде дивидендов
  dividendsExpenses: '0', // Расходы на выплату дивидендов
  participantType: 'resident', // Тип получателя дивидендов (резидент/нерезидент)
  participationShare: '100', // Доля участия в капитале
};

// Определение типов налоговых регионов и ставок
export const taxRegions = [
  { id: 'default', name: 'Стандартная ставка', regionalRate: 17 },
  { id: 'moscow', name: 'Москва', regionalRate: 17 },
  { id: 'saint_petersburg', name: 'Санкт-Петербург', regionalRate: 12.5 }, // Пониженная ставка для некоторых категорий
  { id: 'kaluga', name: 'Калужская область', regionalRate: 13.5 }, // Пониженная ставка для некоторых категорий
  { id: 'leningrad', name: 'Ленинградская область', regionalRate: 13.5 }, // Пониженная ставка для инвесторов
  { id: 'sverdlovsk', name: 'Свердловская область', regionalRate: 13.5 }, // Пониженная ставка для некоторых категорий
  { id: 'tatarstan', name: 'Республика Татарстан', regionalRate: 13.5 }, // Пониженная ставка для резидентов ОЭЗ
  { id: 'custom', name: 'Указать свою ставку', regionalRate: 17 }
];

// Типы особых экономических зон
export const specialZoneTypes = [
  { id: 'none', name: 'Нет' },
  { id: 'industrial', name: 'Промышленно-производственная ОЭЗ', taxRate: 13.5 },
  { id: 'technology', name: 'Технико-внедренческая ОЭЗ', taxRate: 12.5 },
  { id: 'tourist', name: 'Туристско-рекреационная ОЭЗ', taxRate: 13.5 },
  { id: 'port', name: 'Портовая ОЭЗ', taxRate: 13.5 },
  { id: 'advanced', name: 'Территория опережающего развития', taxRate: 0 }, // Может быть 0% первые 5 лет
  { id: 'innovation', name: 'Инновационный центр "Сколково"', taxRate: 0 } // Освобождение для участников проекта
];

// Методы расчета авансовых платежей
export const advancePaymentMethods = [
  { id: 'quarter', name: 'Квартальные авансовые платежи', description: 'Стандартный метод для большинства организаций' },
  { id: 'month', name: 'Ежемесячные авансовые платежи', description: 'Для крупных компаний с выручкой более 15 млн рублей ежеквартально' },
  { id: 'actual', name: 'Ежемесячные платежи по фактической прибыли', description: 'Требует ежемесячной налоговой отчетности' }
];

/**
 * CorporateIncomeTaxCalculatorPage - Калькулятор налога на прибыль организаций в России
 * с учетом региональных ставок, специальных режимов и налоговых льгот.
 */
const CorporateIncomeTaxCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('corporate-tax-calculator') || {
    name: 'Калькулятор налога на прибыль',
    metaTitle: 'Калькулятор налога на прибыль организаций | Gensio',
    metaDescription: 'Полный калькулятор налога на прибыль для российских организаций: расчет налоговой базы, авансовых платежей, с учетом региональных ставок, налоговых льгот и специальных режимов.'
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор налога на прибыль организаций | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор налога на прибыль для российских организаций: расчет налоговой базы, авансовых платежей, с учетом региональных ставок, налоговых льгот и специальных режимов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [calculations, setCalculations] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedData = localStorage.getItem('savedTaxCalculations');
    if (savedData) {
      try {
        setSavedCalculations(JSON.parse(savedData));
      } catch (e) {
        console.error("Error loading saved tax calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'it-companies':
          newState.isITCompany = true;
          break;
        case 'special-zones':
          newState.isResidentSpecialZone = true;
          newState.specialZoneType = 'industrial';
          break;
        case 'dividends':
          newState.dividends = true;
          newState.dividendsIncome = '1000000';
          break;
        case 'rd-deduction':
          newState.specialDeductions = true;
          newState.researchAndDevelopment = '1000000';
          break;
        case 'monthly-payments':
          newState.advancePaymentMethod = 'month';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCalculations(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Расчет за ${formData.taxationYear} год`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      
      // Если есть сохраненные детальные расчеты, восстанавливаем их
      if (calculationToLoad.calculations) {
        setCalculations(calculationToLoad.calculations);
      } else {
        // Иначе пересчитываем детали на основе загруженных данных
        // В реальном сценарии здесь должен быть вызов функции расчета
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор налога на прибыль'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              calculations={calculations}
              setCalculations={setCalculations}
              taxRegions={taxRegions}
              specialZoneTypes={specialZoneTypes}
              advancePaymentMethods={advancePaymentMethods}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <CalculationDetails 
                  calculations={calculations}
                  results={results}
                />
              </>
            )}

            {/* --- Информационный контент о налоге на прибыль --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id || 'corporate-tax-calculator'} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default CorporateIncomeTaxCalculatorPage;